import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import MarketTotalRevenueCellsTdChangeWithTooltip from 'lumen/components/market/total-revenue/cells/td-change-with-tooltip';
import MarketTotalRevenueCellsTdExport from 'lumen/components/market/total-revenue/cells/td-export';
import type { TotalRevenueBreakdownItem } from 'lumen/models';
import { REFERENCE_PERIOD_TRANSLATIONS_SHORT } from 'lumen/utils/constants/total-revenue-constants';

interface MarketTotalRevenueContributionBreakdownTableSourcesSignature {
  Args: {
    exportSourceTransactions: (source: TotalRevenueBreakdownItem) => void;
    selectedReferenceType: string;
    sources: TotalRevenueBreakdownItem[];
  };
}

const COLUMN_PROPERTIES = {
  NAME: 'name',
  REVENUE: 'revenue',
  CHANGE: 'change',
  RELATIVE_CHANGE: 'relativeChange',
  EXPORT: 'export',
};

export default class MarketTotalRevenueContributionBreakdownTableSources extends Component<MarketTotalRevenueContributionBreakdownTableSourcesSignature> {
  @service declare intl: Services['intl'];

  initialSorts = [
    {
      valuePath: `referenceValue.${COLUMN_PROPERTIES.CHANGE}.value`,
      isAscending: false,
    },
  ];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const referencePeriodTranslationKey =
      REFERENCE_PERIOD_TRANSLATIONS_SHORT[
        this.args
          .selectedReferenceType as keyof typeof REFERENCE_PERIOD_TRANSLATIONS_SHORT
      ];

    return [
      {
        name: this.intl.t('total_revenue.source_name'),
        valuePath: COLUMN_PROPERTIES.NAME,
        textAlign: 'left' as const,
      },
      {
        name: this.intl.t('general.revenue'),
        valuePath: `${COLUMN_PROPERTIES.REVENUE}.value`,
        displayValuePath: `${COLUMN_PROPERTIES.REVENUE}.displayValue`,
        textAlign: 'left' as const,
      },
      {
        name: `${this.intl.t('general.revenue')} ${this.intl.t(referencePeriodTranslationKey)}`,
        valuePath: `referenceValue.${COLUMN_PROPERTIES.REVENUE}.value`,
        displayValuePath: `referenceValue.${COLUMN_PROPERTIES.REVENUE}.displayValue`,
        textAlign: 'left' as const,
      },
      {
        name: `${this.intl.t('general.difference')}`,
        valuePath: `referenceValue.${COLUMN_PROPERTIES.CHANGE}.value`,
        displayValuePath: `referenceValue.${COLUMN_PROPERTIES.CHANGE}`,
        textAlign: 'left' as const,
        tdComponent: MarketTotalRevenueCellsTdChangeWithTooltip,
      },
      {
        name: `${this.intl.t('general.variance')} (%)`,
        valuePath: `referenceValue.${COLUMN_PROPERTIES.RELATIVE_CHANGE}.value`,
        displayValuePath: `referenceValue.${COLUMN_PROPERTIES.RELATIVE_CHANGE}`,
        textAlign: 'left' as const,
        tdComponent: MarketTotalRevenueCellsTdChangeWithTooltip,
      },
      {
        name: '',
        valuePath: COLUMN_PROPERTIES.EXPORT,
        textAlign: 'right' as const,
        isSortable: false,
        tdComponent: MarketTotalRevenueCellsTdExport,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    return this.args.sources.map((source) => {
      const referenceValue = source.referenceValues.find(
        (reference) =>
          reference.referenceType === this.args.selectedReferenceType,
      );

      return {
        [COLUMN_PROPERTIES.NAME]: source.translatedSubCategory,
        [COLUMN_PROPERTIES.REVENUE]: {
          value: source.valueOrPAR,
          displayValue: source.formattedValue,
        },
        referenceValue: {
          [COLUMN_PROPERTIES.REVENUE]: {
            value: referenceValue?.value,
            displayValue: referenceValue?.formattedValue,
          },
          [COLUMN_PROPERTIES.CHANGE]: {
            value: referenceValue?.change,
            displayValue: referenceValue?.formattedChange,
          },
          [COLUMN_PROPERTIES.RELATIVE_CHANGE]: {
            value: referenceValue?.relativeChange,
            displayValue: referenceValue?.formattedRelativeChange,
            showTooltip: referenceValue?.isRelativeChangeUnavailable,
            tooltipIconName: 'warning',
            tooltipIconColor: 'warning',
            tooltipTitle: this.intl.t(
              'total_revenue.variance_unavailable.title',
            ),
            tooltipMessage: this.intl.t(
              'total_revenue.variance_unavailable.message',
            ),
          },
        },
        [COLUMN_PROPERTIES.EXPORT]: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          handleExport: () => this.args.exportSourceTransactions(source),
          tooltipMessage: this.intl.t(
            'total_revenue.export_transactions_from_source',
            {
              source: source.translatedSubCategory,
            },
          ),
        },
      };
    });
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @enableAutoSortsUpdate={{true}}
      @initialSorts={{this.initialSorts}}
      @rows={{this.rows}}
      @sortEmptyLast={{true}}
      class="sources-table"
    />
  </template>
}
